import React, { useState, useRef } from 'react';
import { createStyles, Fade, makeStyles, Popover } from '@material-ui/core';
import { PopoverProps } from '@material-ui/core/Popover/Popover';
import IconButton, { Cube3IconButtonProps } from '../../Buttons/IconButton';
import { useSurfaceClasses } from '../../Surface/Surface';
import { iconElepsis } from '../../icons/GeneralSVG';
import { iconFiles } from '@cube3/cubicle/src/core/atoms/Icon/files';
import { ContextMenuListItem } from '@cube3/cubicle/src/core/molecules/ContextMenu/ContextMenuListItem';
import { ContextMenu } from '@cube3/cubicle/src/core/molecules/ContextMenu';
import UseOutsideClick from '@cube3/cubicle/src/helpers/hooks/UseOutsideClick';
import { isMobile } from 'react-device-detect';
import { MobileSlideSurface } from '@cube3/cubicle/src/components/Surface/MobileSlideSurface';

const useStyles = makeStyles((theme) =>
  createStyles({
    root: {},

    iconButton: {
      backgroundColor: 'unset',
      '&:hover': {
        backgroundColor: 'unset'
      }
    }
  })
);
export interface PopoverMenuClassType {
  root?: string;
  iconButton?: string;
  menuButton?: string;
}
interface Properties {
  menuIconPath?: keyof typeof iconFiles;
  extraPopOverProps?: PopoverProps;
  buttonText?: string;
  extraButtonProps?: Cube3IconButtonProps;
  childrenRenderProps: (closeFunction: Function, open: boolean) => JSX.Element;
  classProps?: PopoverMenuClassType;
}

/**
* @summary Shows a button that when clicked, shows a menu with items
The items are the children of this component, so you can
map or define those items in your own components.
* @author Simon 
*/
export const PopoverMenu: React.FunctionComponent<Properties> = (props) => {
  const refContainer = useRef<HTMLElement>(undefined);
  const [open, setOpen] = useState<boolean>(false);

  const classes = useStyles(props);

  const closeMenu = () => {
    setOpen(false);
  };

  const { menuIconPath, extraButtonProps, buttonText, childrenRenderProps } =
    props;

  const buttonRef = UseOutsideClick(closeMenu);
  return (
    <div className={classes.root} ref={buttonRef}>
      {buttonText ? (
        <ContextMenuListItem
          onClick={() => setOpen(true)}
          leftIcon={menuIconPath}
          value={buttonText}
          extraRootProps={{ buttonRef: refContainer, ...extraButtonProps }}
          showChevron={false}
          color="02"
        />
      ) : (
        <IconButton
          onClick={() => setOpen(true)}
          path={menuIconPath ? menuIconPath : iconElepsis}
          extraIconButtonProps={{
            buttonRef: refContainer,
            classes: { root: classes.iconButton }
          }}
          colorVariant="ghost2"
          {...extraButtonProps}
        />
      )}

      {!isMobile ? (
        <>
          {open && (
            <ContextMenu
              anchor={refContainer.current}
              width={'100%'}
              scrollbarHeight={500}
            >
              <div style={{ padding: 1 }}>
                {childrenRenderProps(closeMenu, open)}
              </div>
            </ContextMenu>
          )}
        </>
      ) : (
        <>
          {open && (
            <MobileSlideSurface onClose={() => closeMenu}>
              {childrenRenderProps(closeMenu, open)}
            </MobileSlideSurface>
          )}
        </>
      )}
    </div>
  );
};
